import * as XLSX from 'xlsx';

export interface ImportRow {
    name: string;
    title: string;
    companyName: string;
    email: string;
    country: string;
    countryCode: string;
    language: string;
    sourceUrl: string;
    sourceRow: number;
    sourceSheet: string;
    customFields: Record<string, string>;
}

export interface ContactSheet {
    name: string;
    hidden: boolean;
    selected: boolean;
    headerRowIndex: number;
    emailColumn: number;
    columns: Array<{ index: number; label: string }>;
    lastRow: number;
    rows: ImportRow[];
    customFieldNames: string[];
    issue: string;
}

export interface WorkbookSource {
    fileName: string;
    workbook: XLSX.WorkBook;
    sheets: ContactSheet[];
}

export interface EmailValidation {
    email: string;
    normalizedEmail: string;
    status: 'valid' | 'blank' | 'invalid' | 'suspicious';
    reason: string;
    suggestion?: string;
}

export interface WorkbookImport extends WorkbookSource {
    rows: ImportRow[];
    validations: EmailValidation[];
    counts: Record<EmailValidation['status'], number>;
    customFieldNames: string[];
}

const FIELD_ALIASES = {
    email: ['email', 'emails', 'email address', 'email addresses', 'e-mail', 'mail', 'contact email', 'primary email', 'business email', 'work email', 'personal email', 'email 1', 'email 2', '邮箱', '电子邮箱', '邮箱地址', '电子邮件', '电子邮件地址', '邮件地址', '联系邮箱', '联系人邮箱', '工作邮箱', '公司邮箱', '企业邮箱', '电子信箱', 'correo', 'correo electrónico', 'endereço de email', 'endereço eletrônico', 'البريد الإلكتروني'],
    name: ['name', 'contact name', 'full name', '姓名', '名字', '联系人', '联系人姓名', 'nombre', 'nome'],
    firstName: ['first name', 'given name', '名'],
    lastName: ['last name', 'surname', '姓'],
    title: ['title', 'job title', 'position', '职位', '职务', 'cargo'],
    company: ['company name', 'company', 'firm name', 'law firm', 'organization', 'organisation', '机构/律所', '机构', '律所', '公司', '公司名称', '机构名称', '律所名称', 'empresa'],
    practice: ['practice area', 'practice', 'specialty', 'specialism', 'field', '领域', '业务领域', '专业领域', '所处的领域'],
    country: ['country', '国家', 'pais', 'país'],
    countryCode: ['country code', 'countrycode', '国家代码'],
    language: ['language', '语言', 'idioma'],
    url: ['source url', '来源 url', '来源url', 'website', '官网', '网址', 'url'],
};

function normalizeHeader(value: string): string {
    return value.normalize('NFKC').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/[\s_\-‐‑‒–—./\\:：*]+/g, '').trim();
}

const aliases = Object.fromEntries(Object.entries(FIELD_ALIASES).map(([field, names]) =>
    [field, new Set(names.map(normalizeHeader))]
)) as Record<keyof typeof FIELD_ALIASES, Set<string>>;

function isHeader(value: string, field: keyof typeof FIELD_ALIASES): boolean {
    const key = normalizeHeader(value);
    if (aliases[field].has(key)) return true;
    if (field !== 'email') return false;
    // Accept annotations/bilingual headings, but never mistake an email-status
    // or validation column for the recipient address itself.
    if (/status|check|issue|valid|verified|score|quality|状态|检查|问题|验证|建议/.test(key)) return false;
    return value.split(/[\n/|（(]/).some((part) => aliases.email.has(normalizeHeader(part)))
        || /^(?:primary|secondary|business|work|personal|contact)?email(?:address)?[1-9]?$/.test(key)
        || /^(?:email(?:address)?邮箱|邮箱email(?:address)?)$/.test(key);
}

function cleanCustomHeader(value: string): string {
    const key = value.replace(/[{}\r\n]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
    return ['__proto__', 'prototype', 'constructor'].includes(key.toLowerCase()) ? `字段 ${key}` : key;
}

interface SheetRow { index: number; cells: string[] }

function readRows(sheet: XLSX.WorkSheet): SheetRow[] {
    // Read populated cells instead of expanding !ref into a dense matrix. This
    // preserves Excel row/column numbers, including blank lines before a header,
    // and handles sheets whose formatting extends far past their actual data.
    const rows = new Map<number, string[]>();
    Object.keys(sheet).forEach((address) => {
        if (!/^[A-Z]+[1-9]\d*$/.test(address)) return;
        const cell = sheet[address] as XLSX.CellObject;
        let value = typeof cell.v === 'boolean' ? String(cell.v) : XLSX.utils.format_cell({ ...cell });
        const mailto = cell.l?.Target;
        if (mailto && /^mailto:/i.test(mailto)) {
            try { value = decodeURIComponent(mailto.slice(7).split('?')[0]); } catch { /* retain visible cell */ }
        }
        if (!value.trim()) return;
        const { r, c } = XLSX.utils.decode_cell(address);
        const cells = rows.get(r) || [];
        cells[c] = value;
        rows.set(r, cells);
    });
    return Array.from(rows, ([index, cells]) => ({ index, cells })).sort((a, b) => a.index - b.index);
}

const ADDRESS_SHAPE = /^[^\s@<>;,]+@[^\s@<>;,]+\.[^\s@<>;,]+$/;

function detectHeader(rows: SheetRow[]): { headerRowIndex: number; emailColumn: number } | undefined {
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];
        let best: { headerRowIndex: number; emailColumn: number; score: number } | undefined;
        row.cells.forEach((value, column) => {
            if (!isHeader(value, 'email')) return;
            const addresses = rows.slice(rowIndex + 1, rowIndex + 11)
                .filter((next) => ADDRESS_SHAPE.test((next.cells[column] || '').trim())).length;
            const score = addresses;
            if (!best || score > best.score) best = { headerRowIndex: row.index, emailColumn: column, score };
        });
        // Never prefer a later repeated header: that would silently drop every
        // contact above it. Only compare alternative email columns on this row.
        if (best) return best;
    }
    return undefined;
}

export function parseContactSheet(
    workbook: XLSX.WorkBook,
    name: string,
    mapping?: { headerRowIndex: number; emailColumn: number }
): ContactSheet {
    const data = readRows(workbook.Sheets[name]);
    const hidden = Boolean(workbook.Workbook?.Sheets?.find((sheet) => sheet.name === name)?.Hidden);
    const detected = mapping || detectHeader(data);
    const headerRowIndex = detected?.headerRowIndex ?? data[0]?.index ?? 0;
    const emailColumn = detected?.emailColumn ?? -1;
    const headers = data.find((row) => row.index === headerRowIndex)?.cells || [];
    const usedColumns = new Set<number>();
    data.forEach((row) => row.cells.forEach((_, column) => usedColumns.add(column)));
    const columns = Array.from(usedColumns).sort((a, b) => a - b).map((index) => ({
        index, label: `${XLSX.utils.encode_col(index)} · ${headerRowIndex >= 0 ? headers[index] || '未命名列' : '无表头'}`,
    }));
    const result: ContactSheet = {
        name, hidden, selected: false, headerRowIndex, emailColumn, columns,
        lastRow: (data[data.length - 1]?.index ?? 0) + 1,
        rows: [], customFieldNames: [], issue: '',
    };
    if (!data.length) { result.issue = '空白工作表，已跳过'; return result; }
    if (emailColumn < 0) { result.issue = '未识别到邮箱列，可手动设置或跳过此表'; return result; }
    if (headerRowIndex >= 0 && !headers.some((value) => value.trim())) {
        result.issue = '指定的表头行为空，请调整行号'; return result;
    }
    const fieldColumn = (field: keyof typeof FIELD_ALIASES) => headerRowIndex < 0
        ? -1 : headers.findIndex((value) => Boolean(value) && isHeader(value, field));
    const standard = {
        name: fieldColumn('name'), firstName: fieldColumn('firstName'), lastName: fieldColumn('lastName'),
        title: fieldColumn('title'), company: fieldColumn('company'), practice: fieldColumn('practice'),
        country: fieldColumn('country'), countryCode: fieldColumn('countryCode'),
        language: fieldColumn('language'), url: fieldColumn('url'),
    };
    const reservedColumns = new Set([emailColumn, ...Object.values(standard)].filter((index) => index >= 0));
    const generatedColumns = new Set(['emailcheck', 'emailissue', 'suggestedemail', 'importeligible', '邮箱检查', '邮箱问题', '建议邮箱', '允许导入']);
    const usedNames = new Set(['来源工作表', '来源行号']);
    const uniqueName = (base: string) => {
        let name = base;
        for (let suffix = 2; usedNames.has(name); suffix++) {
            const tail = ` (${suffix})`;
            name = `${base.slice(0, 80 - tail.length)}${tail}`;
        }
        usedNames.add(name);
        return name;
    };
    const customColumns: Array<{ column: number; key: string }> = [];
    if (standard.practice >= 0) customColumns.push({ column: standard.practice, key: uniqueName('Practice Area') });
    columns.forEach(({ index: column }) => {
        const header = headerRowIndex >= 0 ? headers[column] || '' : '';
        if (reservedColumns.has(column) || generatedColumns.has(normalizeHeader(header))) return;
        const base = cleanCustomHeader(header) || `列 ${XLSX.utils.encode_col(column)}`;
        customColumns.push({ column, key: uniqueName(base) });
    });
    if (customColumns.length + 2 > 50) {
        result.issue = '自定义字段超过 50 个（含来源工作表和行号），请精简此表的列后重新选择文件';
        return result;
    }
    result.customFieldNames = [...customColumns.map(({ key }) => key), '来源工作表', '来源行号'];
    result.rows = data.filter((row) => row.index > headerRowIndex).flatMap((row) => {
        // Some exports repeat their table headings at page boundaries.
        if (headerRowIndex >= 0 && row.cells[emailColumn] && isHeader(row.cells[emailColumn], 'email')
            && headers.every((header, column) => normalizeHeader(header) === normalizeHeader(row.cells[column] || ''))) return [];
        const text = (column: number) => (row.cells[column] ?? '').trim();
        return [{
            name: text(standard.name) || [text(standard.firstName), text(standard.lastName)].filter(Boolean).join(' '),
            title: text(standard.title), companyName: text(standard.company), email: text(emailColumn),
            country: text(standard.country), countryCode: text(standard.countryCode),
            language: text(standard.language), sourceUrl: text(standard.url),
            sourceRow: row.index + 1, sourceSheet: name,
            customFields: Object.fromEntries([
                ...customColumns.map(({ column, key }) => [key, text(column).slice(0, 1_000)]),
                ['来源工作表', name], ['来源行号', String(row.index + 1)],
            ]),
        }];
    });
    if (!result.rows.length) result.issue = '表头下没有联系人数据';
    result.selected = !hidden && !result.issue;
    return result;
}

export function parseContactWorkbook(workbook: XLSX.WorkBook, fileName: string): WorkbookSource {
    if (!workbook.SheetNames.length) throw new Error('此文件不包含工作表，请选择 Excel 联系人文件。');
    return { fileName, workbook, sheets: workbook.SheetNames.map((name) => parseContactSheet(workbook, name)) };
}

export function selectedWorkbookRows(sheets: ContactSheet[]): ImportRow[] {
    const selected = sheets.filter((sheet) => sheet.selected && !sheet.issue);
    const count = selected.reduce((total, sheet) => total + sheet.rows.length, 0);
    if (count > 100_000) throw new Error(`所选工作表共 ${count.toLocaleString()} 行，单次最多导入 100,000 行。请减少勾选的工作表后分次导入。`);
    return selected.flatMap((sheet) => sheet.rows);
}

export function annotateContactWorkbook(
    workbook: XLSX.WorkBook, sheets: ContactSheet[], rows: ImportRow[], validations: EmailValidation[]
): XLSX.WorkBook {
    if (rows.length !== validations.length) throw new Error('检查结果与联系人行数不一致，请重新检查。');
    const output = { ...workbook, Sheets: { ...workbook.Sheets } };
    const labels = ['邮箱检查', '邮箱问题', '建议邮箱', '允许导入'];
    const statuses = { valid: '格式有效', blank: '空白', invalid: '格式无效', suspicious: '疑似拼写错误' };
    sheets.filter((sheet) => sheet.selected && !sheet.issue).forEach((parsed) => {
        const sheet = { ...workbook.Sheets[parsed.name] };
        output.Sheets[parsed.name] = sheet;
        const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
        const startCol = range.e.c + 1;
        if (startCol + 3 > 16_383) throw new Error(`工作表“${parsed.name}”没有足够的空列保存检查结果。`);
        if (parsed.headerRowIndex >= 0) labels.forEach((label, offset) => {
            sheet[XLSX.utils.encode_cell({ r: parsed.headerRowIndex, c: startCol + offset })] = { t: 's', v: label };
        });
        rows.forEach((row, index) => {
            if (row.sourceSheet !== parsed.name) return;
            const result = validations[index];
            [statuses[result.status], result.reason, result.suggestion || '', result.status === 'valid' ? '是' : '否']
                .forEach((value, offset) => {
                    sheet[XLSX.utils.encode_cell({ r: row.sourceRow - 1, c: startCol + offset })] = { t: 's', v: value };
                });
        });
        range.e.c = startCol + 3;
        sheet['!ref'] = XLSX.utils.encode_range(range);
    });
    return output;
}
